import React from 'react'
import NextImage from 'next/image'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { CategoryListItem } from './CategoryListItem/CategoryListItem'
import DocumentEmptyState from './DocumentEmptyState'
import { PageContainer } from 'components/ResponsiveLayout'
import KMDisclaimer from './KMDisclaimer'

import type { ResponseSuccessGetDocumentCategoryDetail } from 'api/document_collection/useAPIDocumentCollection'

interface DesktopCategoryLayoutProps {
  isLoading: boolean
  categoryInfo: ResponseSuccessGetDocumentCategoryDetail['data']
  desktopHeader: React.ReactNode
  onGuideClick: () => void
  Link: React.ComponentType<{
    isExternalLink: boolean
    href: string
    target: string
    onClick?: () => void
    children: React.ReactNode
  }>
}

const LoadingState = (props: { length?: number }) => {
  const { length = 10 } = props

  return (
    <>
      {Array.from({ length })?.map((_, index) => (
        <Shimmer
          key={index}
          height={80}
          className="mt-4 !w-full"
          style={{
            borderRadius: 12,
          }}
        />
      ))}
    </>
  )
}

export const DesktopCategoryLayout = ({
  isLoading,
  categoryInfo,
  desktopHeader,
  onGuideClick,
  Link,
}: DesktopCategoryLayoutProps) => {
  return (
    <div className="flex flex-col min-h-screen bg-surface-subdued">
      {desktopHeader}
      <main className="bg-white w-full">
        <PageContainer>
          {isLoading ? (
            <div className="flex flex-col p-4 md:px-8">
              <div className="flex flex-row items-center justify-between">
                <div className="mt-2 flex flex-col !w-full">
                  <Shimmer
                    height={15}
                    className="mb-4 !w-full"
                    style={{ borderRadius: 12 }}
                  />
                  <Shimmer
                    height={10}
                    className="mb-4 !w-full"
                    style={{ borderRadius: 12 }}
                  />
                  <Shimmer
                    height={10}
                    className="mb-4 !w-full"
                    style={{ borderRadius: 12 }}
                  />
                </div>
                <Shimmer
                  height={80}
                  className="ml-4 !w-full max-w-[25vw]"
                  style={{ borderRadius: 12 }}
                />
              </div>
              <LoadingState length={1} />
            </div>
          ) : (
            <>
              <div className="flex flex-row p-4 space-x-3">
                <div
                  className="flex flex-col space-y-2 grow"
                  style={{ wordBreak: 'break-word' }}
                >
                  {Boolean(categoryInfo?.parentName) && (
                    <Text
                      variant="subheading"
                      color="subdued"
                      className="uppercase line-clamp-1"
                    >
                      {categoryInfo?.parentName}
                    </Text>
                  )}
                  <Text variant="heading-lg" className="line-clamp-2">
                    {categoryInfo?.name}
                  </Text>
                  <Text className="line-clamp-2" variant="helper">
                    {categoryInfo?.description}
                  </Text>
                </div>
                {categoryInfo?.thumbnailUrl && (
                  <div className="flex-none w-[84px] h-[84px]">
                    <NextImage
                      src={categoryInfo?.thumbnailUrl}
                      alt="Documents"
                      width="84px"
                      height="84px"
                      className="object-cover object-center"
                      layout="responsive"
                    />
                  </div>
                )}
              </div>
              {categoryInfo?.guideUrl && (
                <div className="px-4 mb-4">
                  <Link
                    href={categoryInfo?.guideUrl}
                    isExternalLink={true}
                    target="_blank"
                    onClick={onGuideClick}
                  >
                    <div className="flex flex-row justify-between p-4 mt-4 space-x-4 border rounded-lg">
                      <div className="flex-none w-8 h-8">
                        <NextImage
                          src={categoryInfo?.guideThumbnailUrl}
                          alt="Guide"
                          width="32px"
                          height="32px"
                          className="object-cover object-center"
                          layout="responsive"
                        />
                      </div>
                      <div className="grow flex flex-col ml-4 space-y-[2px]">
                        <Text variant="action" className="line-clamp-3">
                          {categoryInfo?.guideTitle}
                        </Text>
                        <Text
                          variant="body-sm"
                          className="line-clamp-3"
                          color="subdued"
                        >
                          {categoryInfo?.guideContent}
                        </Text>
                      </div>
                      <div className="flex-none">
                        <Icon className="!text-subdued">chevron_right</Icon>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
              {categoryInfo?.categoryType !== 'CT_UNDEFINED' && (
                <KMDisclaimer className="mb-3 px-4" />
              )}
            </>
          )}
        </PageContainer>
        <div className="bg-surface-subdued w-full py-2">
          <PageContainer>
            {categoryInfo?.categoryType === 'CT_UNDEFINED' ? (
              <DocumentEmptyState />
            ) : (
              <section className="px-4">
                <CategoryListItem parentData={categoryInfo} />
              </section>
            )}
          </PageContainer>
        </div>
      </main>
    </div>
  )
}
